/**
 * Device Detail Modal
 * Shows detailed device information with option to remove/logout device
 * Premium Apple-like design
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Laptop, Smartphone, Tablet, Monitor, MapPin, Clock, Shield, AlertTriangle } from "lucide-react";
import { remoteLogoutDevice, removeDevice, isDeviceOnline, formatLastActive, getCurrentDeviceId, trustDevice, untrustDevice } from "../utils/deviceManager";

// Device icons based on device type
const DeviceIcon = ({ device, className = "" }) => {
    const deviceName = device?.device_name?.toLowerCase() || '';
    const deviceType = device?.device_type || 'desktop';
    
    // Custom SVG icons for different device types
    if (deviceName.includes('iphone') || deviceType === 'mobile') {
        return (
            <svg className={className} viewBox="0 0 24 36" fill="currentColor">
                <rect x="2" y="0" width="20" height="36" rx="3" ry="3" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                <circle cx="12" cy="32" r="1.5" fill="currentColor"/>
                <rect x="8" y="2" width="8" height="1" rx="0.5" fill="currentColor"/>
            </svg>
        );
    }
    
    if (deviceName.includes('ipad') || deviceType === 'tablet') {
        return <Tablet className={className} />;
    }
    
    if (deviceName.includes('mac') || deviceName.includes('macbook') || deviceType === 'laptop') {
        return (
            <svg className={className} viewBox="0 0 50 36" fill="currentColor">
                <rect x="4" y="2" width="42" height="26" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="6" y="4" width="38" height="22" rx="1" fill="currentColor" opacity="0.1"/>
                <path d="M0 30 L50 30 L48 34 L2 34 Z" fill="currentColor"/>
            </svg>
        );
    }
    
    if (deviceName.includes('imac')) {
        return <Monitor className={className} />;
    }
    
    // Default desktop/windows
    return (
        <svg className={className} viewBox="0 0 40 36" fill="currentColor">
            <rect x="2" y="2" width="36" height="24" rx="2" ry="2" fill="none" stroke="currentColor" strokeWidth="1.5"/>
            <rect x="15" y="26" width="10" height="6" fill="currentColor"/>
            <rect x="10" y="32" width="20" height="2" rx="1" fill="currentColor"/>
        </svg>
    );
};

const DeviceDetailModal = ({ isOpen, onClose, device, onDeviceRemoved, onSignOut, theme = "dark" }) => {
    const [isRemoving, setIsRemoving] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isTrusted, setIsTrusted] = useState(device?.is_trusted || false);
    const [isTogglingTrust, setIsTogglingTrust] = useState(false);
    const [showTrustConfirm, setShowTrustConfirm] = useState(false);
    const [showLocationMap, setShowLocationMap] = useState(false);
    const [error, setError] = useState('');
    
    const isDark = theme === "dark";
    const online = isDeviceOnline(device?.last_active);
    const currentDeviceId = getCurrentDeviceId();
    const isCurrentDevice = device?.id === currentDeviceId;
    
    if (!device) return null;
    
    const handleRemoveFromAccount = async () => {
        if (isCurrentDevice) {
            setError("You cannot remove the device you're currently using.");
            return;
        }
        
        setIsRemoving(true);
        setError('');
        
        const success = await removeDevice(device.id);
        
        if (success) {
            onDeviceRemoved?.(device.id);
            onClose();
        } else {
            setError('Failed to remove device. Please try again.');
        }
        
        setIsRemoving(false);
    };
    
    const handleLogoutDevice = async () => {
        if (isCurrentDevice) {
            setError("Use the Sign Out button to log out of this device.");
            return;
        }
        
        setIsLoggingOut(true);
        setError('');
        
        const success = await remoteLogoutDevice(device.id);
        
        if (success) {
            // Refresh device list
            onDeviceRemoved?.(device.id, true); // true = just refresh, don't remove from list
            onClose();
        } else {
            setError('Failed to log out device. Please try again.');
        }
        
        setIsLoggingOut(false);
    };
    
    return (
        <>
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-md"
                        onClick={onClose}
                    />
                    
                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: 40 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: 40 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className={`relative w-full max-w-[820px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] border backdrop-blur-3xl ${
                            isDark ? 'bg-black text-white border-blue-500/25' : 'bg-white text-black border-blue-200'
                        }`}
                    >
                        {/* Blue Glows */}
                        <div className="absolute top-0 right-0 w-48 h-48 bg-blue-600/[0.04] blur-[80px] pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/[0.03] blur-[60px] pointer-events-none" />
                        {/* Header */}
                        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                            <div className="flex items-center">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center mr-1 overflow-hidden border ${isDark ? 'bg-black border-white/10' : 'bg-gray-100 border-gray-200'}`}>
                                    <img 
                                        src="/logoicon.png" 
                                        alt="FOZZIL" 
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <span className={`text-lg font-bold tracking-tighter ${isDark ? 'text-white' : 'text-black'}`}>Device Info</span>
                            </div>
                            <button
                                onClick={onClose}
                                className={`px-4 py-1 rounded-[32px] text-sm font-semibold transition-all border ${
                                    isDark 
                                        ? 'bg-white/5 text-white hover:bg-white/10 border-white/10' 
                                        : 'bg-gray-100 text-black hover:bg-gray-200 border-gray-200'
                                }`}
                            >
                                Close
                            </button>
                        </div>
                        {/* Content — Landscape Two-Column */}
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
                                {/* Left Column — Device Identity */}
                                <div className="flex flex-col items-center justify-center text-center">
                                    {/* Device Icon */}
                                    <div className={`w-20 h-16 flex items-center justify-center mb-3 ${online ? 'text-blue-500' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <DeviceIcon device={device} className="w-full h-full" />
                                    </div>
                                    
                                    {/* Device Name */}
                                    <h2 className="text-lg font-bold mb-0.5">
                                        {device.device_name}
                                    </h2>
                                    
                                    {/* Badges row */}
                                    <div className="flex items-center gap-1.5 mt-1 flex-wrap justify-center">
                                        {isCurrentDevice && (
                                            <span className="text-[10px] font-medium text-blue-400 bg-blue-500/[0.08] border border-blue-500/10 px-2.5 py-0.5 rounded-full">
                                                This Device
                                            </span>
                                        )}
                                        <button
                                            onClick={() => setShowTrustConfirm(true)}
                                            className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full border transition-all hover:scale-105 active:scale-95 cursor-pointer ${
                                                isTrusted
                                                    ? 'text-blue-400 bg-blue-500/[0.08] border-blue-500/10 hover:bg-blue-500/15'
                                                    : isDark ? 'text-gray-400 bg-white/[0.03] border-white/5 hover:bg-white/[0.08]' : 'text-gray-500 bg-gray-100/50 border-gray-100 hover:bg-gray-100'
                                            }`}
                                        >
                                            {isTrusted ? '✓ Trusted' : 'Trust this device'}
                                        </button>
                                    </div>
                                    
                                    {/* OS Info */}
                                    <p className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {device.os} {device.os_version}
                                    </p>
                                    
                                    {/* Online Status */}
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className={`w-2 h-2 rounded-full ${online ? 'bg-green-500' : 'bg-gray-400'}`} />
                                        <span className={`text-xs ${online ? 'text-green-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {online ? 'Online now' : `Last seen ${formatLastActive(device.last_active)}`}
                                        </span>
                                    </div>
                                </div>

                                {/* Right Column — Details */}
                                <div className="flex flex-col gap-4">
                                    {/* Security Section */}
                                    <div>
                                        <h3 className={`text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-black'}`}>Security</h3>
                                        <div className={`rounded-[32px] p-3 space-y-2 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Shield className={`w-4 h-4 ${!device.session_valid ? 'text-red-500' : online ? 'text-green-500' : 'text-blue-500'}`} />
                                                    <span className="text-sm">Session Status</span>
                                                </div>
                                                <span className={`text-xs font-medium ${!device.session_valid ? 'text-red-500' : online ? 'text-green-500' : 'text-blue-500'}`}>
                                                    {!device.session_valid ? 'Logged Out' : online ? 'Online' : 'Signed In'}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Shield className={`w-4 h-4 ${isTrusted ? 'text-blue-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                                    <span className="text-sm">Trusted Device</span>
                                                </div>
                                                <span className={`text-xs font-medium ${isTrusted ? 'text-blue-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    {isTrusted ? 'Trusted' : 'Not Trusted'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* About Section */}
                                    <div>
                                        <h3 className={`text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-black'}`}>About</h3>
                                        <div className={`rounded-[32px] p-4 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Device Type</p>
                                                    <p className="text-sm font-medium capitalize">{device.device_type}</p>
                                                </div>
                                                <div>
                                                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Operating System</p>
                                                    <p className="text-sm font-medium">{device.os} {device.os_version}</p>
                                                </div>
                                                {device.location_city && (
                                                    <div className="col-span-2">
                                                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Location</p>
                                                        <div className="flex items-center justify-between mt-0.5">
                                                            <div className="flex items-center gap-1.5">
                                                                <MapPin className="w-3 h-3" />
                                                                <p className="text-sm font-medium">
                                                                    {device.location_city}, {device.location_region || device.location_country}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setShowLocationMap(true); }}
                                                                className="text-[11px] font-semibold text-blue-500 hover:text-blue-400 transition-colors"
                                                            >
                                                                View Location
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="col-span-2">
                                                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>First Signed In</p>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        <Clock className="w-3 h-3" />
                                                        <p className="text-sm font-medium">
                                                            {new Date(device.created_at).toLocaleDateString('en-US', {
                                                                month: 'long',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Error Message */}
                            {error && (
                                <motion.div 
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-4 flex items-center gap-2 text-red-500 text-xs justify-center"
                                >
                                    <AlertTriangle className="w-3 h-3" />
                                    <span>{error}</span>
                                </motion.div>
                            )}
                            
                            {/* Action Buttons — Full width bottom */}
                            <div className="flex items-center justify-center gap-3 mt-5 flex-wrap">
                                {!isCurrentDevice && device.session_valid && (
                                    <>
                                        <button
                                            onClick={handleLogoutDevice}
                                            disabled={isLoggingOut}
                                            className={`w-[120px] py-2 rounded-full font-bold transition-all text-[11px] shadow-lg hover:scale-105 active:scale-95 bg-[#065DC0] hover:bg-[#0071e3] text-white ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {isLoggingOut ? 'Logging Out...' : 'Sign Out'}
                                        </button>
                                        
                                        <button
                                            onClick={handleRemoveFromAccount}
                                            disabled={isRemoving}
                                            className={`w-[120px] py-2 rounded-full font-bold transition-all text-[11px] border shadow-lg hover:scale-105 active:scale-95 ${
                                                isDark 
                                                    ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' 
                                                    : 'border-red-200 text-red-600 hover:bg-red-50'
                                            } ${isRemoving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {isRemoving ? 'Removing...' : 'Remove'}
                                        </button>
                                    </>
                                )}

                                {isCurrentDevice && (
                                    <button
                                        onClick={() => {
                                            onClose();
                                            onSignOut?.();
                                        }}
                                        className="w-[120px] py-2 rounded-full font-bold transition-all text-[11px] shadow-lg bg-[#065DC0] hover:bg-[#0071e3] hover:scale-105 active:scale-95 text-white"
                                    >
                                        Sign Out
                                    </button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
        {/* Trust Confirmation Modal */}
        <AnimatePresence>
            {showTrustConfirm && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-md"
                        onClick={() => setShowTrustConfirm(false)}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: 30 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className={`relative w-full max-w-[380px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] border backdrop-blur-3xl p-8 text-center ${
                            isDark ? 'bg-black text-white border-blue-500/25' : 'bg-white text-black border-blue-200'
                        }`}
                    >
                        {/* Blue Glows */}
                        <div className="absolute top-0 right-0 w-40 h-40 bg-blue-600/[0.04] blur-[70px] pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-28 h-28 bg-blue-500/[0.03] blur-[50px] pointer-events-none" />

                        <div className="relative z-10">
                            <div className={`w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center ${isTrusted ? 'bg-red-500/10' : isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                                <Shield size={24} className={isTrusted ? 'text-red-500' : 'text-blue-500'} />
                            </div>

                            <h3 className="text-lg font-bold mb-1">
                                {isTrusted ? 'Remove Trust?' : 'Trust This Device?'}
                            </h3>
                            <p className={`text-[13px] mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {isTrusted 
                                    ? 'This device will no longer appear in your trusted devices list and won\'t receive verification codes.'
                                    : 'This device will be added to your trusted devices and can receive verification codes for your account.'}
                            </p>

                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => setShowTrustConfirm(false)}
                                    className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                                        isDark 
                                            ? 'bg-white/5 text-white hover:bg-white/10 border-white/10' 
                                            : 'bg-gray-100 text-black hover:bg-gray-200 border-gray-200'
                                    }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={async () => {
                                        setIsTogglingTrust(true);
                                        if (isTrusted) {
                                            const ok = await untrustDevice(device.id);
                                            if (ok) setIsTrusted(false);
                                        } else {
                                            const ok = await trustDevice(device.id);
                                            if (ok) setIsTrusted(true);
                                        }
                                        setIsTogglingTrust(false);
                                        setShowTrustConfirm(false);
                                        onDeviceRemoved?.(device.id, true);
                                    }}
                                    disabled={isTogglingTrust}
                                    className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all hover:scale-105 active:scale-95 shadow-lg ${
                                        isTrusted 
                                            ? 'bg-red-500 hover:bg-red-600 text-white'
                                            : 'bg-[#065DC0] hover:bg-[#0071e3] text-white'
                                    } ${isTogglingTrust ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {isTogglingTrust ? '...' : isTrusted ? 'Remove Trust' : 'Trust Device'}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
        {/* Location Map Popup */}
        <AnimatePresence>
            {showLocationMap && device?.location_city && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-md"
                        onClick={() => setShowLocationMap(false)}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: 30 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className={`relative w-full max-w-[600px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] border backdrop-blur-3xl ${
                            isDark ? 'bg-black text-white border-blue-500/25' : 'bg-white text-black border-blue-200'
                        }`}
                    >
                        {/* Blue Glows */}
                        <div className="absolute top-0 right-0 w-40 h-40 bg-blue-600/[0.04] blur-[70px] pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-28 h-28 bg-blue-500/[0.03] blur-[50px] pointer-events-none" />
                        {/* Header */}
                        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                            <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-blue-500" />
                                <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-black'}`}>
                                    {device.location_city}, {device.location_region || device.location_country}
                                </span>
                            </div>
                            <button
                                onClick={() => setShowLocationMap(false)}
                                className={`px-4 py-1 rounded-[32px] text-sm font-semibold transition-all border ${
                                    isDark 
                                        ? 'bg-white/5 text-white hover:bg-white/10 border-white/10' 
                                        : 'bg-gray-100 text-black hover:bg-gray-200 border-gray-200'
                                }`}
                            >
                                Close
                            </button>
                        </div>
                        {/* Map */}
                        <div style={{ height: '400px' }}>
                            <iframe
                                title="Device Location Map"
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                scrolling="no"
                                src={`https://maps.google.com/maps?q=${encodeURIComponent(`${device.location_city}, ${device.location_region || device.location_country}`)}&t=&z=12&ie=UTF8&iwloc=&output=embed`}
                                loading="lazy"
                                allowFullScreen=""
                            />
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
        </>
    );
};

export default DeviceDetailModal;
